import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  equals,
  flatten,
  lensPath,
  mergeRight,
  omit,
  pickBy,
  pluck,
  prepend,
  set,
  union,
  values,
} from "ramda";

import { isBlank, notBlank } from "../util/ext";

import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";

import EditBar from "../components/EditBar";
import { useSnackAlert } from "../common/use-snackAlert";
import { useLocation, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import UserDetailsCard from "./UserDetailsCard";
import {
  useUser,
  useUserProfile,
  useUserProfileUpdate,
  useUserUpdate,
} from "./userServices";
import UserRolesCard from "./UserRolesCard";
import UserOrganizationsCard from "./UserOrganizationsCard";
import UserProfileCard from "./UserProfileCard";
import { useConfirm } from "../common/use-confirm";
import ConfirmDialog from "../components/ConfirmDialog";
import { validateUser } from "./userValidation";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "95%",
  },

  panel: {
    padding: 0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  editContainer: {
    flexGrow: 1,
    paddingBottom: theme.spacing(3),
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "white",
    backgroundColor: "white",
    color: theme.palette.primary.main,
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
  },

  editPanel: {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },

  commonInfo: {
    flexBasis: "50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
}));

const objectDiff = media => (val, key) => !equals(media[key], val);

const UserEditView = props => {
  const { onCancel } = props;
  const { userId } = useParams();
  const location = useLocation();
  const state = useMemo(() => location?.state ?? {}, [location]);
  const classes = useStyles();

  //-- Manage the alert message
  const { showAlert, SnackAlert } = useSnackAlert();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up overall state
  const [user, setUser] = useState({});
  const [userLoaded, setUserLoaded] = useState(false);
  const userResult = useUser(userId);
  useEffect(() => {
    if (userResult.isSuccess) {
      const adjustedUser = set(
        lensPath(["privacyPolicyAccepted"]),
        !!userResult.data.privacyPolicyAccepted,
        set(
          lensPath(["privacyPolicyAcceptedDate"]),
          userResult.data.privacyPolicyAccepted,
          userResult.data,
        ),
      );
      setUser(prev => mergeRight(prev, adjustedUser));
    }
  }, [userResult.isSuccess, userResult.data]);

  const userProfileResult = useUserProfile(userId);
  useEffect(() => {
    if (userProfileResult.isSuccess) {
      const profile = {
        profile: userProfileResult.data,
      };
      setUser(prev => mergeRight(prev, profile));
    }
  }, [userProfileResult.isSuccess, userProfileResult.data]);

  const [initialUser, setInitialUser] = useState({});
  useEffect(() => {
    if (notBlank(user.userId) && notBlank(user.profile)) {
      setUserLoaded(true);
      if (isBlank(initialUser)) {
        console.log("Setting Initial User");
        console.log(user);
        setInitialUser(user);
      }
    }
  }, [user, initialUser]);

  const [currentChanges, setCurrentChanges] = useState({});
  useEffect(() => {
    if (notBlank(user) && notBlank(initialUser)) {
      const initialAuthUser = omit(
        ["organizations", "profile", "roles", "dateUpdated"],
        initialUser,
      );
      const authUser = omit(
        ["organizations", "profile", "roles", "dateUpdated"],
        user,
      );

      const userChanges = pickBy(objectDiff(initialAuthUser), authUser);
      const profileChanges = pickBy(
        objectDiff(initialUser.profile),
        user.profile,
      );

      console.log(userChanges);
      console.log(profileChanges);

      setCurrentChanges({
        user: userChanges,
        profile: profileChanges,
      });
    }
  }, [initialUser, user]);

  //-- Handle saving the content
  const updateUser = useUserUpdate();
  const updateUserProfile = useUserProfileUpdate();
  const handleSave = useCallback(async () => {
    const validation = flatten(values(validateUser(user)));
    const passwordCheck =
      user.password !== user.confirmPassword
        ? ["Password and Confirm do not match."]
        : [];
    const allErrors = union(validation, passwordCheck);

    if (isBlank(allErrors)) {
      try {
        if (notBlank(currentChanges.user)) {
          const authUser = omit(["organizations", "profile", "roles"], user);

          await updateUser.mutateAsync({
            resourceIds: { id: initialUser.userId },
            data: authUser,
          });
        }

        if (
          notBlank(currentChanges.profile) ||
          notBlank(currentChanges.user.username)
        ) {
          const profileUser = mergeRight(currentChanges.profile, {
            username: currentChanges.user.username,
          });

          await updateUserProfile.mutateAsync({
            resourceIds: { id: initialUser.userId },
            data: profileUser,
          });
        }

        setInitialUser(user);
        showAlert("success", ["Content Successfully Saved"]);
      } catch (err) {
        if (notBlank(err.body)) {
          showAlert("error", pluck("message", err.body.errors));
        } else {
          showAlert("error", [err.message]);
        }
      }
    } else {
      showAlert(
        "error",
        prepend(
          "There are errors that should be fixed before saving.",
          allErrors,
        ),
      );
    }
  }, [
    initialUser,
    user,
    currentChanges,
    updateUser,
    updateUserProfile,
    showAlert,
  ]);

  const handleBackClicked = useCallback(() => {
    if (isBlank(currentChanges.user) && isBlank(currentChanges.profile)) {
      onCancel(state.previousLocation);
    } else {
      console.log("There are differences.");
      showConfirm(
        "Discard Changes?",
        "You have made changes to this user without saving.  Do you wish to discard these changes and go back to the user list?",
        {
          onConfirm: () => {
            onCancel(state.previousLocation);
          },
        },
      );
    }
  }, [currentChanges, showConfirm, onCancel, state]);

  const handleUserChange = useCallback((path, value) => {
    setUser(prev => set(lensPath(path), value, prev));
  }, []);

  return (
    <Container
      id="user-edit-view"
      maxWidth={false}
      classes={{ root: classes.root }}
    >
      <SnackAlert />
      <ConfirmDialog handler={confirmHandler} />
      {userLoaded && (
        <Fragment>
          <EditBar
            onBackClick={handleBackClicked}
            onSaveClick={handleSave}
            saveDisabled={
              isBlank(currentChanges.user) && isBlank(currentChanges.profile)
            }
          />
          <Container
            id="user-edit-container"
            maxWidth={false}
            className={classes.editContainer}
          >
            <div className={classes.scrollArea}>
              <Paper id="content-edit-panel" className={classes.editPanel}>
                <Grid container spacing={2} direction="column">
                  <Grid
                    item
                    container
                    justifyContent="space-between"
                    direction="row"
                  >
                    <Grid item md={true}>
                      <UserDetailsCard
                        user={user}
                        onChange={handleUserChange}
                        onAlert={showAlert}
                      />
                    </Grid>
                  </Grid>
                  <Grid item></Grid>
                  <Grid item container justifyContent="space-between">
                    <Grid item md={4} container direction="column" spacing={2}>
                      <Grid item>
                        <UserProfileCard
                          user={user}
                          onChange={handleUserChange}
                        />
                      </Grid>

                      <Grid item>
                        <UserRolesCard user={user} onAlert={showAlert} />
                      </Grid>
                    </Grid>
                    <Grid item md={8} container direction="column" spacing={2}>
                      <Grid item>
                        <UserOrganizationsCard user={user} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </div>
          </Container>
        </Fragment>
      )}
    </Container>
  );
};

export default UserEditView;
